import { Icon } from '@iconify/react'

// Per-type visual placeholders. Designed to be fully responsive — fill the parent
// container at any size (modal preview tile or full widget body). No fixed pixels.
// The shimmer animation is provided by the `.widget-skeleton` wrapper class
// (defined in ViewGrid.css), so individual elements use solid bg-muted shapes.

const BAR_HEIGHTS = [38, 70, 52, 82, 46, 64]
const SCATTER_DOTS = [
    { t: 18, l: 12 }, { t: 64, l: 22 }, { t: 36, l: 38 },
    { t: 72, l: 52 }, { t: 26, l: 64 }, { t: 54, l: 78 }, { t: 42, l: 88 },
]
const TABLE_ROW_WIDTHS = [85, 95, 72, 88, 82, 92]

export const SKELETON_BY_TYPE = {
    'account-scorecard': () => (
        <div className="grid grid-cols-4 gap-1.5 h-full w-full">
            {[0, 1, 2, 3].map(i => (
                <div key={i} className="flex flex-col justify-center gap-1 rounded-md border border-primary/15 p-1.5">
                    <div className="h-1.5 w-2/3 bg-primary/40 rounded-sm" />
                    <div className="h-3 w-3/4 bg-primary/60 rounded-sm" />
                </div>
            ))}
        </div>
    ),

    'account-breakdown': () => (
        <div className="flex flex-col justify-center gap-2 h-full w-full">
            {[82, 64, 48, 34].map((w, i) => (
                <div key={i} className="flex items-center gap-2">
                    <div className="h-2 w-8 shrink-0 bg-primary/40 rounded-sm" />
                    <div className="h-2.5 bg-primary/60 rounded-sm" style={{ width: `${w}%` }} />
                </div>
            ))}
        </div>
    ),

    'ad-audience-matrix': () => (
        <div className="flex flex-col gap-1 h-full w-full">
            {[0, 1, 2, 3].map(r => (
                <div key={r} className="flex items-center gap-1">
                    <div className="h-3 w-10 shrink-0 bg-primary/40 rounded-sm" />
                    {[0, 1, 2, 3, 4].map(c => (
                        <div key={c} className="h-3 flex-1 rounded-sm bg-primary/20" style={{ opacity: 0.25 + ((r + c) % 4) * 0.2 }} />
                    ))}
                </div>
            ))}
        </div>
    ),

    'ad-audience-lens': () => (
        <div className="flex flex-col gap-2 h-full w-full">
            <div className="flex gap-1.5">
                {[10, 12, 12].map((w, i) => (
                    <div key={i} className="h-4 rounded-full bg-primary/30" style={{ width: `${w * 4}px` }} />
                ))}
            </div>
            {[82, 60, 44].map((w, i) => (
                <div key={i} className="flex items-center gap-2">
                    <div className="h-6 w-6 shrink-0 rounded bg-primary/40" />
                    <div className="h-2.5 rounded-sm bg-primary/50" style={{ width: `${w}%` }} />
                </div>
            ))}
        </div>
    ),

    'account-campaigns': () => (
        <div className="flex flex-col h-full w-full gap-2">
            <div className="h-2.5 w-full bg-primary/50 rounded-sm" />
            {[92, 80, 72, 86].map((w, i) => (
                <div key={i} className="flex items-center gap-2">
                    <div className="h-2.5 flex-1 bg-primary/30 rounded-sm" style={{ maxWidth: `${w}%` }} />
                    <div className="h-2.5 w-8 shrink-0 bg-primary/50 rounded-sm" />
                </div>
            ))}
        </div>
    ),

    'creative-highlights': () => (
        <div className="flex gap-2 h-full w-full">
            {[0, 1, 2].map(i => (
                <div key={i} className="flex flex-1 gap-1.5 rounded-md border border-primary/15 p-1.5">
                    <div className="w-1/3 bg-primary/50 rounded-sm" />
                    <div className="flex flex-1 flex-col justify-center gap-1">
                        <div className="h-1.5 w-full bg-primary/30 rounded-sm" />
                        <div className="h-5 w-full bg-primary/20 rounded-sm" />
                        <div className="h-1.5 w-2/3 bg-primary/30 rounded-sm" />
                    </div>
                </div>
            ))}
        </div>
    ),

    'chart-bar': () => (
        <div className="flex items-end gap-2 h-full w-full">
            {BAR_HEIGHTS.map((h, i) => (
                <div key={i} className="flex-1 bg-primary/40 rounded-t-sm" style={{ height: `${h}%` }} />
            ))}
        </div>
    ),

    'chart-line': () => (
        <div className="h-full w-full">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
                <polyline
                    points="0,72 16,46 32,56 50,24 66,40 82,16 100,30"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    className="text-primary/70"
                />
            </svg>
        </div>
    ),

    'chart-pie': () => (
        <div className="flex items-center justify-center h-full w-full">
            <svg viewBox="0 0 100 100" className="aspect-square max-h-[70%] max-w-[55%]">
                <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="14" className="text-primary/40" />
            </svg>
        </div>
    ),

    'chart-scatter': () => (
        <div className="relative h-full w-full">
            {SCATTER_DOTS.map((p, i) => (
                <div
                    key={i}
                    className="absolute h-2 w-2 rounded-full bg-primary/70 -translate-x-1/2 -translate-y-1/2"
                    style={{ top: `${p.t}%`, left: `${p.l}%` }}
                />
            ))}
        </div>
    ),

    'chart-comparison': () => (
        <div className="flex gap-2 h-full w-full">
            <div className="flex-1 bg-primary/40 rounded-md" />
            <div className="flex-1 bg-primary/40 rounded-md" />
        </div>
    ),

    'chart-filmstrip': () => (
        <div className="flex gap-2 h-full w-full">
            {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className="flex-1 bg-primary/40 rounded-md flex flex-col gap-1.5 p-1.5">
                    <div className="flex-1 bg-primary/20 rounded-sm" />
                    <div className="h-1.5 bg-primary/25 rounded-sm" />
                    <div className="h-1.5 w-2/3 bg-primary/25 rounded-sm" />
                </div>
            ))}
        </div>
    ),

    'metric-table': () => (
        <div className="flex flex-col h-full w-full gap-2">
            <div className="h-2.5 w-full bg-primary/60 rounded-sm" />
            {TABLE_ROW_WIDTHS.map((w, i) => (
                <div key={i} className="h-2.5 bg-primary/40 rounded-sm" style={{ width: `${w}%` }} />
            ))}
        </div>
    ),

    'kpi-tile': () => (
        <div className="flex flex-col h-full w-full justify-center gap-2">
            <div className="h-2 w-1/3 bg-primary/60 rounded-sm" />
            <div className="h-7 w-2/3 bg-primary/40 rounded-md" />
            <div className="h-2 w-1/4 bg-primary/60 rounded-sm" />
        </div>
    ),

    'heading': () => (
        <div className="flex items-center h-full w-full">
            <div className="h-3 w-1/2 bg-primary/40 rounded-sm" />
        </div>
    ),

    'text': () => (
        <div className="flex flex-col h-full w-full justify-center gap-2">
            <div className="h-2 w-full bg-primary/40 rounded-sm" />
            <div className="h-2 w-[88%] bg-primary/40 rounded-sm" />
            <div className="h-2 w-[72%] bg-primary/40 rounded-sm" />
        </div>
    ),

    'ai-summary': () => (
        <div className="flex h-full w-full items-center justify-center">
            <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-primary/15">
                <div className="absolute inset-0 rounded-full bg-primary/10" style={{ transform: 'scale(1.35)' }} />
                <Icon icon="lucide:sparkles" className="relative h-5 w-5 text-primary/70" />
            </div>
        </div>
    ),
}

SKELETON_BY_TYPE['ad-segment-matrix'] = SKELETON_BY_TYPE['ad-audience-matrix']
SKELETON_BY_TYPE['ad-segment-lens'] = SKELETON_BY_TYPE['ad-audience-lens']

export const WIDGET_DESCRIPTIONS = {
    'account-scorecard': 'Account totals with compare deltas',
    'account-breakdown': 'Segments, placements, regions',
    'account-campaigns': 'Top campaigns with account total',
    'chart-bar': 'Compare values across groups',
    'chart-line': 'Trends over time',
    'chart-pie': 'Share of total',
    'chart-scatter': 'Two metrics, every group',
    'chart-comparison': 'Side-by-side groups',
    'chart-filmstrip': 'Top creatives strip',
    'creative-highlights': 'Top video ads with retention',
    'metric-table': 'Sortable rows of metrics',
    'ad-audience-matrix': 'Ads × audience heatmap',
    'ad-audience-lens': 'Top ads per audience',
    'ad-segment-matrix': 'Ads × new / engaged / existing',
    'ad-segment-lens': 'Top ads per customer segment',
    'kpi-tile': 'One key number',
    'heading': 'Section title',
    'text': 'Markdown notes',
    'ai-summary': 'AI read of the period',
}
